// 請求書の発行日・支払期限を、請求先の締め日・支払日設定から自動算出するユーティリティ。
// すべて副作用のない純粋関数（ローカルタイムで日付を組み立て、UTC変換によるズレを避ける）。

/** Date をローカルタイムの YYYY-MM-DD 文字列へ整形する。 */
function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** YYYY-MM-DD 文字列をローカルタイムの Date へ変換する（不正な値は null）。 */
function parseDateStr(s: string): Date | null {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** 指定した年月（monthは0始まりのオフセット可）の末日を返す。 */
function endOfMonth(year: number, month: number): Date {
  // 翌月の0日 = 当月末日。month が範囲外でも Date が桁上げ・桁下げで吸収する。
  return new Date(year, month + 1, 0);
}

/**
 * 請求書の発行日を「直近の月末」に合わせて算出する。
 *
 * 月末・月初に発行する運用に合わせ、当月15日〜翌月14日を「当月」とみなし、
 * その対象月の末日を発行日とする。
 *   例) 6/15〜7/14 → 6月末(6/30)
 *       6/14       → 5月末(5/31)
 */
export function computeInvoiceIssueDate(base: Date): string {
  const y = base.getFullYear();
  const m = base.getMonth(); // 0-11
  // 14日以前は前月、15日以降は当月を対象月とする
  const targetMonth = base.getDate() >= 15 ? m : m - 1;
  return toDateStr(endOfMonth(y, targetMonth));
}

/**
 * 請求先の支払日マスタ計算式から支払期限を算出する。
 * 締め月（＝発行日の属する月）を基準に「当月／翌月／翌々月」＋「末 or N日」を解釈する。
 *   例) 発行日 6/30, 計算式 "翌月末"   → 7/31
 *       発行日 6/30, 計算式 "翌々月10日" → 8/10
 * 「随時」など月オフセットを解釈できない計算式は null を返す（自動入力しない）。
 */
export function computeInvoiceDueDate(issueDate: string, formula: string): string | null {
  const base = parseDateStr(issueDate);
  if (!base) return null;

  // 「翌々月」を「翌月」より先に判定する（部分一致の取り違え防止）
  const offset = formula.includes('翌々月')
    ? 2
    : formula.includes('翌月')
      ? 1
      : formula.includes('当月')
        ? 0
        : null;
  if (offset === null) return null;

  const y = base.getFullYear();
  const m = base.getMonth() + offset;

  if (formula.includes('末')) {
    return toDateStr(endOfMonth(y, m));
  }

  const dayMatch = formula.match(/(\d{1,2})\s*日/);
  if (dayMatch) {
    const wanted = Number(dayMatch[1]);
    const lastDay = endOfMonth(y, m).getDate();
    // 指定日が対象月の末日を超える場合は末日へ丸める
    return toDateStr(new Date(y, m, Math.min(wanted, lastDay)));
  }

  return null;
}
